import { Base } from "./Base";
import { throwExpected, SpaceNotFoundException, SpaceNameReservedException, TokenNotFoundException, SpaceHasTooManyClustersException, CantRemoveOwnerFromSpaceException, UserNameNotFoundException, NotFoundException, ClusterNotFoundException, } from "./exceptions";
export const spaceKinds = ["Personal", "Team"];
export const spaceFeatures = ["DevCluster"];
/**
 *
 * The class for consuming all `space` resources.
 *
 * @remarks
 * This class should be generated using OpenAPI generator in the future.
 *
 * @alpha
 */
class SpaceService extends Base {
    /**
     * Get one space by space name
     */
    async getOne({ name, queryString }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}${queryString ? `/?${queryString}` : ""}`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: () => new SpaceNotFoundException(name),
        });
        return json;
    }
    /**
     * Get spaces
     */
    async getMany(queryString) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces${queryString ? `/?${queryString}` : ""}`;
        const json = await throwExpected(async () => fetch.get(url));
        return json;
    }
    /**
     * Create one space
     */
    async createOne(space) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces`;
        const json = await throwExpected(async () => fetch.post(url, space), {
            422: () => new SpaceNameReservedException(space.name),
        });
        return json;
    }
    /**
     * Add feature to users' Personal Spaces.
     * @param feature - Feature to add
     * @param users - Array of usernames or email addresses
     */
    async addSpaceFeature(feature, users) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/features`;
        const json = await throwExpected(async () => fetch.post(url, { feature, users }), {
            404: () => new NotFoundException(),
        });
        return json;
    }
    /**
     * Remove feature from users' Personal Spaces.
     * @param feature - Feature remove add
     * @param users - Array of usernames or email addresses
     */
    async removeSpaceFeature(feature, users) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/features/remove`;
        const json = await throwExpected(async () => fetch.delete(url, { data: { feature, users } }), {
            404: () => new NotFoundException(),
        });
        return json;
    }
    /**
     * Update one space
     */
    async updateOne(spaceName, space) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${spaceName}`;
        const json = await throwExpected(async () => fetch.patch(url, space), {
            500: () => new TokenNotFoundException(),
            422: () => new SpaceNameReservedException(space.name),
        });
        return json;
    }
    /**
     * Delete one space by space name
     */
    async deleteOne({ name }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}`;
        await throwExpected(async () => fetch.delete(url), {
            500: () => new TokenNotFoundException(),
            404: () => new SpaceNotFoundException(name),
        });
    }
    /**
     * Get bored-secret by space name and cluster id
     */
    async getBoredSecret({ name, clusterId, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}/k8sclusters/${clusterId}/bored-secret`;
        const json = await throwExpected(async () => fetch.get(url), {
            // TODO: differentiate between space cluster and secret not being found
            404: () => new SpaceNotFoundException(name),
        });
        return json;
    }
    /**
     * Get cluster token by space name and cluster id
     */
    async getClusterToken({ name, clusterId, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}/k8sclusters/${clusterId}/token`;
        const json = await throwExpected(async () => fetch.get(url), {
            // TODO: differentiate between space, cluster, user and token not being found
            404(error) {
                const message = error === null || error === void 0 ? void 0 : error.body.message;
                if (typeof message === "string" && message.includes("Space ")) {
                    return new SpaceNotFoundException(name);
                }
                return new ClusterNotFoundException(clusterId);
            },
        });
        return json;
    }
    /**
     * Get all clusters in one space by space name
     */
    async getClusters({ name }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}/k8sclusters`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: () => new SpaceNotFoundException(name),
        });
        return json;
    }
    /**
     * Get all invitation domains in one space by space name
     */
    async getInvitationDomains({ name }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}/security/invitation-domains`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: () => new SpaceNotFoundException(name),
        });
        return json;
    }
    /**
     * Add one invitation domain in a Space by space name
     */
    async addInvitationDomain({ name, domain, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}/security/invitation-domains`;
        const json = await throwExpected(async () => fetch.post(url, {
            domain,
        }), {
            404: () => new SpaceNotFoundException(name),
        });
        return json;
    }
    /**
     * Delete one invitation domain in a Space by space name and invitation domain id
     */
    async deleteInvitationDomain({ name, invitationDomainId, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}/security/invitation-domains/${invitationDomainId}`;
        await throwExpected(async () => fetch.delete(url), {
            // Space or InvitationDomain missing
            404: () => new NotFoundException(),
        });
    }
    /**
     * Get one cluster by cluster id in one space by space name
     */
    async getOneCluster({ clusterId, name, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}/k8sclusters/${clusterId}`;
        const json = await throwExpected(async () => fetch.get(url), {
            404(error) {
                const message = error === null || error === void 0 ? void 0 : error.body.message;
                if (typeof message === "string" && message.includes("Space ")) {
                    return new SpaceNotFoundException(name);
                }
                return new ClusterNotFoundException(clusterId);
            },
        });
        return json;
    }
    /**
     * Create a cluster in one space by space name
     */
    async createOneCluster({ cluster, name, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}/k8sclusters`;
        const json = await throwExpected(async () => fetch.post(url, cluster), {
            404: () => new SpaceNotFoundException(name),
            422: () => new SpaceHasTooManyClustersException(name),
        });
        return json;
    }
    /**
     * Update a cluster
     */
    async updateOneCluster({ cluster }) {
        var _a;
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${(_a = cluster.space) === null || _a === void 0 ? void 0 : _a.name}/k8sclusters/${cluster.id}`;
        const json = await throwExpected(async () => fetch.patch(url, cluster), {
            // TODO: differentiate between space and cluster not being found
            404: () => { var _a, _b; return new SpaceNotFoundException((_b = (_a = cluster.space) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : "undefined"); },
        });
        return json;
    }
    /**
     * Replace a cluster
     */
    async replaceOneCluster({ cluster }) {
        var _a;
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${(_a = cluster.space) === null || _a === void 0 ? void 0 : _a.name}/k8sclusters/${cluster.id}`;
        const json = await throwExpected(async () => fetch.put(url, cluster), {
            // TODO: differentiate between space and cluster not being found
            404: () => { var _a, _b; return new SpaceNotFoundException((_b = (_a = cluster.space) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : "undefined"); },
        });
        return json;
    }
    /**
     * Delete a cluster by cluster id in one space by space name
     */
    async deleteOneCluster({ clusterId, name }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}/k8sclusters/${clusterId}`;
        await throwExpected(async () => fetch.delete(url), {
            // TODO: differentiate between space and cluster not being found,
            // improve error handling here overall
            404: () => new SpaceNotFoundException(name),
        });
    }
    /**
     * Remove one user by username from a space by space name
     * @throws SpaceNotFoundException, UserNameNotFoundException, ForbiddenException, CantRemoveOwnerFromSpaceException
     */
    async removeOneUser({ username, name }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}/users/${username}`;
        await throwExpected(async () => fetch.delete(url), {
            404(error) {
                var _a;
                const message = (_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message;
                if (typeof message === "string" && message.includes(name)) {
                    return new SpaceNotFoundException(name);
                }
                return new UserNameNotFoundException(username);
            },
            422: () => new CantRemoveOwnerFromSpaceException(username),
        });
    }
    /**
     * Get billing plan of space by space name
     */
    async getBillingPlan({ name }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}/plan`;
        const json = await throwExpected(async () => fetch.get(url));
        return json;
    }
    /**
     * Downgrades the billing plan of space by space name
     */
    async downgradeBillingPlan({ name }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/spaces/${name}/plan`;
        const json = await fetch.delete(url);
        return json;
    }
}
export { SpaceService };
